import React, { useEffect } from 'react'
import { Paper } from '@material-ui/core'
import ActionBar from 'components/ActionBar'

import { useHistory, useLocation } from 'react-router-dom'
import ViewHeader from 'components/ViewHeader'
import { useDispatch, useSelector } from 'react-redux'

import Loading from 'components/Loading'
import { useConfirm } from 'material-ui-confirm'
import getTableOptions from 'utils/getTableOptions'
import speakerSlice, {
  deleteSpeakerById,
  checkSpeakerExistsInTalks,
  getSpeakers
} from 'store/speaker'
import { DeleteOutlineOutlined as DeleteIcon, EditOutlined as EditIcon } from '@material-ui/icons'
import ResourceNotFound from 'components/ResourceNotFound'
import Tooltip from '@material-ui/core/Tooltip'
import { toast } from 'react-toastify'
import BasicTable from 'components/BasicTable'
import { renderCol } from 'utils/renderColumns'
import Avatar from 'components/Avatar'
import { Roles, speakerStatus } from 'constants/index'
import { caseInsensitiveSort } from 'utils'

const SpeakerListing = () => {
  const dispatch = useDispatch()
  const confirm = useConfirm()
  const history = useHistory()
  const location = useLocation()

  const { user } = useSelector((state) => state.auth)
  const { speakers, currentPage, pageSize, inProgress } = useSelector((state) => state.speaker)

  useEffect(() => {
    const unsub = getSpeakers(user?.conferenceId, (data) => {
      const filteredData = data?.filter((e) => {
        return !e?.status || (e?.status && e?.status !== speakerStatus.PENDING)
      })
      dispatch(speakerSlice.actions.setSpeakers(caseInsensitiveSort(filteredData, 'fullName')))
    })
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId])

  const tableHead = [
    {
      name: '',
      options: {
        setCellProps: () => ({ style: { width: '45px' } })
      }
    },
    'Full Name',
    'Email',
    'Institution',
    'Position',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const formatTableData = (speakers) =>
    speakers.map((speaker) => [
      <Avatar
        height={56}
        width={56}
        variant="cicular"
        path="speakers"
        imageProps={{ style: { objectFit: 'cover', objectPosition: '50% 12%' } }}
        imageName={speaker?.imageName}
        conferenceId={user?.role === Roles.ADMIN ? user.conferenceId : ''}
      />,
      speaker?.fullName,
      speaker?.email,
      speaker?.company,
      speaker?.position,
      <>
        <Tooltip title="Edit">
          <EditIcon
            color="action"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/edit/${speaker.id}`)}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <DeleteIcon
            color="action"
            style={{ marginLeft: 5, cursor: 'pointer' }}
            onClick={async () => {
              try {
                await confirm({
                  title: 'Delete speaker',
                  description: 'Are you sure you want to delete this speaker?'
                })
                try {
                  const isSpeakerExistInTalk = await dispatch(checkSpeakerExistsInTalks(speaker.id))
                  if (isSpeakerExistInTalk) {
                    toast.error(
                      `Can not delete this speaker because it is referenced in other Talk(s). In order to delete this first remove it from all Talks.`
                    )
                  } else {
                    dispatch(deleteSpeakerById(user?.conferenceId, speaker.id))
                  }
                } catch {
                  toast.error(
                    `Something went wrong while deleting the speaker. Please try again later.`
                  )
                }
              } catch (err) {
                // skip error
              }
            }}
          />
        </Tooltip>
      </>
    ])

  const options = getTableOptions()

  return (
    <>
      <ViewHeader title="Speakers">
        {inProgress && <Loading />}
        <ActionBar
          actionText="New Speaker"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Speaker"
        />
      </ViewHeader>

      {speakers && speakers.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(speakers)}
            columns={tableHead}
            options={{
              ...options,
              onChangePage: (page) => dispatch(speakerSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(speakerSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        !inProgress && <ResourceNotFound message="No speaker available" />
      )}
    </>
  )
}

export default SpeakerListing
